// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Card, Col, Input, InputNumber, Row, Select, Switch} from "antd";
import {LinkOutlined} from "@ant-design/icons";
import * as ProviderBackend from "../../services/ProviderBackend";
import * as Setting from "../../conf/Setting";
import i18next from "i18next";
import {authConfig} from "../../conf/Auth";
import * as ProviderEditTestEmail from "../../widget/TestEmailWidget";
import copy from "copy-to-clipboard";
import {CaptchaPreview} from "../../components/common/CaptchaPreview";

const {Option} = Select;
const {TextArea} = Input;

class ProviderEditPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      providerId: props.match.params.providerId,
      provider: null,
      mode: props.location.mode !== undefined ? props.location.mode : "edit",
    };
  }

  UNSAFE_componentWillMount() {
    this.getProvider();
  }

  getProvider() {
    ProviderBackend.getProvider(Setting.Admin, this.state.providerId)
      .then((provider) => {
        this.setState({
          provider: provider,
        });
      });
  }

  parseProviderField(key, value) {
    if (["port"].includes(key)) {
      value = Setting.myParseInt(value);
    }
    return value;
  }

  updateProviderField(key, value) {
    value = this.parseProviderField(key, value);

    const provider = this.state.provider;
    provider[key] = value;
    this.setState({
      provider: provider,
    });
  }

  clearProviderField() {
    const provider = this.state.provider;
    provider.clientId = "";
    provider.clientSecret = "";
    provider.appId = "";
    provider.signName = "";
    provider.host = "";
    provider.title = "";
    provider.content = "";
    provider.domain = "";
    provider.templateCode = "";
    provider.endpoint = "";
    provider.intranetEndpoint = "";
    provider.regionId = "";
    this.setState({
      provider: provider,
    });
  }

  getClientIdLabel() {
    switch (this.state.provider.category) {
    case "Email":
      return Setting.getLabel(i18next.t("signup:Username"), i18next.t("signup:Username - Tooltip"), true);
    case "SMS":
      if (this.state.provider.type === "Volc Engine SMS") {
        return Setting.getLabel(i18next.t("provider:Access key"), i18next.t("provider:Access key - Tooltip"));
      } else if (this.state.provider.type === "Huawei Cloud SMS") {
        return Setting.getLabel(i18next.t("provider:App key"), i18next.t("provider:App key - Tooltip"));
      } else if (this.state.provider.type === "Aliyun SMS") {
        return Setting.getLabel(i18next.t("provider:AccessKey ID"), i18next.t("provider:AccessKey ID - Tooltip"), true);
      } else if (this.state.provider.type === "Tencent Cloud SMS") {
        return Setting.getLabel(i18next.t("provider:Secret ID"), i18next.t("provider:Secret ID - Tooltip"), true);
      }
      break;
    case "Captcha":
      if (this.state.provider.type === "Aliyun Captcha") {
        return Setting.getLabel(i18next.t("provider:Access key"), i18next.t("provider:Access key - Tooltip"));
      } else {
        return Setting.getLabel(i18next.t("provider:Site key"), i18next.t("provider:Site key - Tooltip"));
      }
    case "OAuth":
      if (this.state.provider.type === "WeChat") {
        return Setting.getLabel(i18next.t("provider:AppID"), i18next.t("provider:AppID - Tooltip"), true);
      } else if (this.state.provider.type === "WeCom") {
        return Setting.getLabel(i18next.t("provider:CorpID"), i18next.t("provider:CorpID - Tooltip"), true);
      } else if (this.state.provider.type === "WeChatOfficial") {
        return Setting.getLabel(i18next.t("provider:AppID"), i18next.t("provider:AppID - Tooltip"), true);
      }
      break;
    case "Storage":
      if (this.state.provider.type === "MinIO") {
        return Setting.getLabel(i18next.t("provider:MinIO Client ID"), i18next.t("provider:MinIO Client ID - Tooltip"), true);
      }
      break;
    default:
      return Setting.getLabel(i18next.t("provider:Client ID"), i18next.t("provider:Client ID - Tooltip"));
    }
  }

  getClientSecretLabel() {
    switch (this.state.provider.category) {
    case "Email":
      return Setting.getLabel(i18next.t("login:Password"), i18next.t("login:Password - Tooltip"), true);
    case "SMS":
      if (this.state.provider.type === "Volc Engine SMS") {
        return Setting.getLabel(i18next.t("provider:Secret access key"), i18next.t("provider:SecretAccessKey - Tooltip"));
      } else if (this.state.provider.type === "Huawei Cloud SMS") {
        return Setting.getLabel(i18next.t("provider:App secret"), i18next.t("provider:AppSecret - Tooltip"));
      } else if (this.state.provider.type === "Aliyun SMS") {
        return Setting.getLabel(i18next.t("provider:AccessKey Secret"), i18next.t("provider:AccessKey Secret - Tooltip"), true);
      } else if (this.state.provider.type === "Tencent Cloud SMS") {
        return Setting.getLabel(i18next.t("provider:Secret Key"), i18next.t("provider:Secret Key - Tooltip"), true);
      }
      break;
    case "Captcha":
      if (this.state.provider.type === "Aliyun Captcha") {
        return Setting.getLabel(i18next.t("provider:Secret access key"), i18next.t("provider:SecretAccessKey - Tooltip"));
      } else {
        return Setting.getLabel(i18next.t("provider:Secret key"), i18next.t("provider:Secret key - Tooltip"));
      }
    case "OAuth":
      if (this.state.provider.type === "WeChat") {
        return Setting.getLabel(i18next.t("provider:App Secret"), i18next.t("provider:App Secret - Tooltip"), true);
      } else if (this.state.provider.type === "WeCom") {
        return Setting.getLabel(i18next.t("provider:Corp Secret"), i18next.t("provider:Corp Secret - Tooltip"), true);
      } else if (this.state.provider.type === "WeChatOfficial") {
        return Setting.getLabel(i18next.t("provider:App Secret"), i18next.t("provider:App Secret - Tooltip"), true);
      }
      break;
    case "Storage":
      if (this.state.provider.type === "MinIO") {
        return Setting.getLabel(i18next.t("provider:MinIO Client secret"), i18next.t("provider:MinIO Client secret - Tooltip"), true);
      }
      break;
    default:
      return Setting.getLabel(i18next.t("provider:Client secret"), i18next.t("provider:Client secret - Tooltip"));
    }
  }

  getClientIdText(provider) {
    switch (this.state.provider.category) {
    case "Email":
      return i18next.t("signup:Username");
    case "SMS":
      if (this.state.provider.type === "Volc Engine SMS") {
        return i18next.t("provider:Access key");
      } else if (this.state.provider.type === "Huawei Cloud SMS") {
        return i18next.t("provider:App key");
      } else if (this.state.provider.type === "Aliyun SMS") {
        return i18next.t("provider:AccessKey ID");
      } else if (this.state.provider.type === "Tencent Cloud SMS") {
        return i18next.t("provider:Secret ID");
      } else {
        return i18next.t("provider:Client ID");
      }
    case "Captcha":
      if (this.state.provider.type === "Aliyun Captcha") {
        return i18next.t("provider:Access key");
      } else {
        return i18next.t("provider:Site key");
      }
    case "OAuth":
      if (this.state.provider.type === "WeChat") {
        return i18next.t("provider:AppID");
      } else if (this.state.provider.type === "WeCom") {
        return i18next.t("provider:CorpID");
      } else if (this.state.provider.type === "WeChatOfficial") {
        return i18next.t("provider:AppID");
      } else {
        return i18next.t("provider:Client ID");
      }
    default:
      return i18next.t("provider:Client ID");
    }
  }

  getClientSecretText(provider) {
    switch (this.state.provider.category) {
    case "Email":
      return i18next.t("login:Password");
    case "SMS":
      if (this.state.provider.type === "Volc Engine SMS") {
        return i18next.t("provider:Secret access key");
      } else if (this.state.provider.type === "Huawei Cloud SMS") {
        return i18next.t("provider:App secret");
      } else if (this.state.provider.type === "Aliyun SMS") {
        return i18next.t("provider:AccessKey Secret");
      } else if (this.state.provider.type === "Tencent Cloud SMS") {
        return i18next.t("provider:Secret Key");
      } else {
        return i18next.t("provider:Client secret");
      }
    case "Captcha":
      if (this.state.provider.type === "Aliyun Captcha") {
        return i18next.t("provider:Secret access key");
      } else {
        return i18next.t("provider:Secret key");
      }
    case "OAuth":
      if (this.state.provider.type === "WeChat") {
        return i18next.t("provider:App Secret");
      } else if (this.state.provider.type === "WeCom") {
        return i18next.t("provider:Corp Secret");
      } else if (this.state.provider.type === "WeChatOfficial") {
        return i18next.t("provider:App Secret");
      } else {
        return i18next.t("provider:Client secret");
      }
    default:
      return i18next.t("provider:Client secret");
    }
  }

  getTemplateCodeLabel() {
    switch (this.state.provider.type) {
    case "Aliyun SMS":
      return Setting.getLabel(i18next.t("provider:Aliyun SMS Template Code"),
        i18next.t("provider:Aliyun SMS Template Code - Tooltip"));
    case "Tencent Cloud SMS":
      return Setting.getLabel(i18next.t("provider:Tencent Cloud SMS Template Code"),
        i18next.t("provider:Tencent Cloud SMS Template Code - Tooltip"));
    default:
      return Setting.getLabel(i18next.t("provider:Template Code"), i18next.t("provider:Template Code - Tooltip"));
    }
  }

  getTemplateCodeText(provider) {
    switch (this.state.provider.type) {
    case "Aliyun SMS":
      return i18next.t("provider:Aliyun SMS Template Code");
    case "Tencent Cloud SMS":
      return i18next.t("provider:Tencent Cloud SMS Template Code");
    default:
      return i18next.t("provider:Template Code");
    }
  }

  getAppIdRow() {
    let text, tooltip;
    let necessary = false;
    let maxLength = 200;
    if (this.state.provider.category === "SMS" && this.state.provider.type === "Tencent Cloud SMS") {
      text = i18next.t("provider:App ID");
      tooltip = i18next.t("provider:App ID - Tooltip");
      maxLength = 50;
    } else if (this.state.provider.type === "WeCom" && this.state.provider.subType === "Internal") {
      text = i18next.t("provider:Agent ID");
      tooltip = i18next.t("provider:Agent ID - Tooltip");
      necessary = true;
    } else if (this.state.provider.type === "Infoflow") {
      text = i18next.t("provider:Agent ID");
      tooltip = i18next.t("provider:Agent ID - Tooltip");
    } else if (this.state.provider.category === "SMS" && this.state.provider.type === "Volc Engine SMS") {
      text = i18next.t("provider:SMS account");
      tooltip = i18next.t("provider:SMS account - Tooltip");
    } else if (this.state.provider.category === "SMS" && this.state.provider.type === "Huawei Cloud SMS") {
      text = i18next.t("provider:Channel No.");
      tooltip = i18next.t("provider:Channel No. - Tooltip");
    } else if (this.state.provider.category === "Email" && this.state.provider.type === "SUBMAIL") {
      text = i18next.t("provider:App ID");
      tooltip = i18next.t("provider:App ID - Tooltip");
    } else {
      return null;
    }

    return <Row style={{marginTop: "20px"}} >
      <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
        {Setting.getLabel(text, tooltip, necessary)} :
      </Col>
      <Col span={22} >
        <Input maxLength={maxLength} showCount={true}
          value={this.state.provider.appId} onChange={e => {
            this.updateProviderField("appId", e.target.value);
          }} />
      </Col>
    </Row>;
  }

  loadSamlConfiguration() {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(this.state.provider.metadata, "text/xml");
    const cert = xmlDoc.getElementsByTagName("ds:X509Certificate")[0].childNodes[0].nodeValue;
    const endpoint = xmlDoc.getElementsByTagName("md:SingleSignOnService")[0].getAttribute("Location");
    const issuerUrl = xmlDoc.getElementsByTagName("md:EntityDescriptor")[0].getAttribute("entityID");
    this.updateProviderField("idP", cert);
    this.updateProviderField("endpoint", endpoint);
    this.updateProviderField("issuerUrl", issuerUrl);
  }

  renderProvider() {
    return (
      <Card size="small" title={
        <div>
          {this.state.mode === "add" ? i18next.t("provider:New Provider") : i18next.t("provider:Edit Provider")}&nbsp;&nbsp;&nbsp;&nbsp;
          <Button onClick={() => this.submitProviderEdit(false)}>{i18next.t("general:Save")}</Button>
          <Button style={{marginLeft: "20px"}} type="primary" onClick={() => this.submitProviderEdit(true)}>{i18next.t("general:Save & Exit")}</Button>
          {this.state.mode === "add" ? <Button style={{marginLeft: "20px"}} onClick={() => this.deleteProvider()}>{i18next.t("general:Cancel")}</Button> :
            <Button style={{marginLeft: "20px"}} onClick={() => this.exit()}>{i18next.t("general:Cancel")}</Button>}
        </div>
      } style={(Setting.isMobile()) ? {margin: "5px"} : {}} type="inner">
        {/* <Row style={{marginTop: "10px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("general:Name"), i18next.t("general:Name - Tooltip"))} :
          </Col>
          <Col span={22} >
            <Input value={this.state.provider.uid} onChange={e => {
              this.updateProviderField("uid", e.target.value);
            }} />
          </Col>
        </Row> */}
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("provider:Display name"), i18next.t("provider:Display name - Tooltip"), true)} :
          </Col>
          <Col span={22} >
            <Input maxLength={20} showCount={true}
              value={this.state.provider.displayName} onChange={e => {
                this.updateProviderField("displayName", e.target.value);
              }} />
          </Col>
        </Row>
        <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("provider:Category"), i18next.t("provider:Category - Tooltip"))} :
          </Col>
          <Col span={22} >
            <Select virtual={false} style={{width: "100%"}} value={this.state.provider.category} onChange={(value => {
              this.clearProviderField();
              this.updateProviderField("category", value);
              if (value === "OAuth") {
                this.updateProviderField("type", "WeChat");
                if (this.state.mode === "add") {
                  this.updateProviderField("displayName", `PC微信扫码 - ${Setting.getRandomName()}`);
                }
              } else if (value === "Email") {
                this.updateProviderField("type", "Default Email");
                this.updateProviderField("host", "smtp.example.com");
                this.updateProviderField("port", 465);
                this.updateProviderField("disableSsl", false);
                this.updateProviderField("title", "统一身份认证注册验证码");
                this.updateProviderField("content", "您的邮箱验证码为:%s，10分钟内有效，请勿向他人泄露。");
                this.updateProviderField("receiver", this.props.account.email);
                if (this.state.mode === "add") {
                  this.updateProviderField("displayName", `邮箱 - ${Setting.getRandomName()}`);
                }
              } else if (value === "SMS") {
                this.updateProviderField("type", "Aliyun SMS");
                if (this.state.mode === "add") {
                  this.updateProviderField("displayName", `阿里云短信 - ${Setting.getRandomName()}`);
                }
              } else if (value === "Storage") {
                this.updateProviderField("type", "Local File System");
                this.updateProviderField("domain", Setting.getFullServerUrl());
                if (this.state.mode === "add") {
                  this.updateProviderField("displayName", `存储 - ${Setting.getRandomName()}`);
                }
                this.updateProviderField("clientId", "1");
                this.updateProviderField("clientSecret", "1");
                this.updateProviderField("endpoint", "1");
                this.updateProviderField("intranetEndpoint", "1");
              } else if (value === "SAML") {
                this.updateProviderField("type", "Aliyun IDaaS");
              } else if (value === "Payment") {
                this.updateProviderField("type", "Alipay");
              } else if (value === "Captcha") {
                this.updateProviderField("type", "Default Captcha");
                if (this.state.mode === "add") {
                  this.updateProviderField("displayName", `人机验证 - ${Setting.getRandomName()}`);
                }
              }
            })}>
              {
                [
                  {id: "OAuth", name: "OAuth"},
                  {id: "Email", name: "Email"},
                  {id: "SMS", name: "SMS"},
                  {id: "Storage", name: "Storage"},
                  // {id: "SAML", name: "SAML"},
                  // {id: "Payment", name: "Payment"},
                  {id: "Captcha", name: "Captcha"},
                ].map((providerCategory, index) => <Option key={index} value={providerCategory.id}>{i18next.t(`provider:${providerCategory.name}`)}</Option>)
              }
            </Select>
          </Col>
        </Row>
        {
          this.state.provider.category === "Captcha" ? null :
            <Row style={{marginTop: "20px"}} >
              <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                {Setting.getLabel(i18next.t("provider:Type"), i18next.t("provider:Type - Tooltip"))} :
              </Col>
              <Col span={22} >
                <Select virtual={false} style={{width: "100%"}} value={this.state.provider.type} onChange={(value => {
                  this.clearProviderField();
                  this.updateProviderField("type", value);
                  if (value === "Local File System") {
                    this.updateProviderField("domain", Setting.getFullServerUrl());
                  }
                  if (value === "Custom") {
                    this.updateProviderField("customAuthUrl", "https://door.casdoor.com/login/oauth/authorize");
                    this.updateProviderField("customScope", "openid profile email");
                    this.updateProviderField("customTokenUrl", "https://door.casdoor.com/api/login/oauth/access_token");
                    this.updateProviderField("customUserInfoUrl", "https://door.casdoor.com/api/userinfo");
                  }
                  if (value === "WeCom") {
                  // 企业微信默认选择
                    this.updateProviderField("subType", "Internal");
                    this.updateProviderField("method", "Normal");
                    if (this.state.mode === "add") {
                      this.updateProviderField("displayName", `PC企业微信扫码 - ${Setting.getRandomName()}`);
                    }
                  }
                  if (value === "WeChat") {
                    if (this.state.mode === "add") {
                      this.updateProviderField("displayName", `PC微信扫码 - ${Setting.getRandomName()}`);
                    }
                  }
                  if (["Aliyun SMS", "Tencent Cloud SMS", "smsYd"].includes(value)) {
                    if (this.state.mode === "add") {
                      this.updateProviderField("displayName", `${i18next.t(`provider:${value}`)} - ${Setting.getRandomName()}`);
                    }
                  }
                  if (value === "MinIO") {
                    if (this.state.mode === "add") {
                      this.updateProviderField("displayName", `MinIO - ${Setting.getRandomName()}`);
                    }
                    if (this.state.provider.regionId === "") {
                      this.updateProviderField("regionId", "111");
                    }
                  }
                })}>
                  {
                    Setting.getProviderTypeOptions(this.state.provider.category).map((providerType, index) => <Option key={index} value={providerType.id}>{i18next.t(`provider:${providerType.name}`)}</Option>)
                  }
                </Select>
              </Col>
            </Row>
        }
        {
          this.state.provider.type !== "Infoflow" && this.state.provider.type !== "Aliyun Captcha" ? null : (
          // this.state.provider.type !== "Infoflow" && this.state.provider.type !== "Aliyun Captcha" ? null : (
            <React.Fragment>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={2}>
                  {Setting.getLabel(i18next.t("provider:Sub type"), i18next.t("provider:Sub type - Tooltip"))} :
                </Col>
                <Col span={22} >
                  <Select virtual={false} style={{width: "100%"}} value={this.state.provider.subType} onChange={value => {
                    this.updateProviderField("subType", value);
                  }}>
                    {
                      Setting.getProviderSubTypeOptions(this.state.provider.type).map((providerSubType, index) => <Option key={index} value={providerSubType.id}>{providerSubType.name}</Option>)
                    }
                  </Select>
                </Col>
              </Row>
              {/* {
                this.state.provider.type !== "WeCom" ? null : (
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={2}>
                      {Setting.getLabel(i18next.t("provider:Method"), i18next.t("provider:Method - Tooltip"))} :
                    </Col>
                    <Col span={22} >
                      <Select virtual={false} style={{width: "100%"}} value={this.state.provider.method} onChange={value => {
                        this.updateProviderField("method", value);
                      }}>
                        {
                          // [{name: "Normal"}, {name: "Silent"}].map((method, index) => <Option key={index} value={method.name}>{method.name}</Option>)
                          [{name: "Normal"}].map((method, index) => <Option key={index} value={method.name}>{method.name}</Option>)
                        }
                      </Select>
                    </Col>
                  </Row>)
              } */}
            </React.Fragment>
          )
        }
        {
          this.state.provider.type !== "Custom" ? null : (
            <React.Fragment>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Auth URL"), i18next.t("provider:Auth URL - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.customAuthUrl} onChange={e => {
                    this.updateProviderField("customAuthUrl", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Scope"), i18next.t("provider:Scope - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.customScope} onChange={e => {
                    this.updateProviderField("customScope", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Token URL"), i18next.t("provider:Token URL - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.customTokenUrl} onChange={e => {
                    this.updateProviderField("customTokenUrl", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:UserInfo URL"), i18next.t("provider:UserInfo URL - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.customUserInfoUrl} onChange={e => {
                    this.updateProviderField("customUserInfoUrl", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("general:Favicon"), i18next.t("general:Favicon - Tooltip"))} :
                </Col>
                <Col span={22} >
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 1}>
                      {Setting.getLabel(i18next.t("general:URL"), i18next.t("general:URL - Tooltip"))} :
                    </Col>
                    <Col span={23} >
                      <Input prefix={<LinkOutlined />} value={this.state.provider.customLogo} onChange={e => {
                        this.updateProviderField("customLogo", e.target.value);
                      }} />
                    </Col>
                  </Row>
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 1}>
                      {i18next.t("general:Preview")}:
                    </Col>
                    <Col span={23} >
                      <a target="_blank" rel="noreferrer" href={this.state.provider.customLogo}>
                        <img src={this.state.provider.customLogo} alt={this.state.provider.customLogo} height={90} style={{marginBottom: "20px"}} />
                      </a>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </React.Fragment>
          )
        }
        {
          (this.state.provider.category === "Storage" && this.state.provider.type !== "MinIO") ||
          (this.state.provider.category === "Captcha" && this.state.provider.type === "Default Captcha") ||
          (this.state.provider.category === "SMS" && this.state.provider.type === "smsYd") ? null : (
              <React.Fragment>
                <Row style={{marginTop: "20px"}} >
                  <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                    {this.getClientIdLabel()}
                  </Col>
                  <Col span={22} >
                    <Input maxLength={this.state.provider.category === "Email" ? 50 : 200} showCount={true}
                      value={this.state.provider.clientId} onChange={e => {
                        this.updateProviderField("clientId", e.target.value);
                      }} />
                  </Col>
                </Row>
                <Row style={{marginTop: "20px"}} >
                  <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                    {this.getClientSecretLabel()}
                  </Col>
                  <Col span={22} >
                    <Input maxLength={this.state.provider.category === "Email" ? 50 : 200} showCount={true}
                      value={this.state.provider.clientSecret} onChange={e => {
                        this.updateProviderField("clientSecret", e.target.value);
                      }} />
                  </Col>
                </Row>
              </React.Fragment>
            )
        }
        {
          // this.state.provider.type !== "WeChat" && this.state.provider.type !== "Aliyun Captcha" ? null : (
          this.state.provider.type !== "Aliyun Captcha" ? null : (
            <React.Fragment>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {this.state.provider.type === "Aliyun Captcha"
                    ? Setting.getLabel(i18next.t("provider:Scene"), i18next.t("provider:Scene - Tooltip"))
                    : Setting.getLabel(i18next.t("provider:Client ID 2"), i18next.t("provider:Client ID 2 - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.clientId2} onChange={e => {
                    this.updateProviderField("clientId2", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {this.state.provider.type === "Aliyun Captcha"
                    ? Setting.getLabel(i18next.t("provider:App key"), i18next.t("provider:App key - Tooltip"))
                    : Setting.getLabel(i18next.t("provider:Client secret 2"), i18next.t("provider:Client secret 2 - Tooltip"))}
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.clientSecret2} onChange={e => {
                    this.updateProviderField("clientSecret2", e.target.value);
                  }} />
                </Col>
              </Row>
            </React.Fragment>
          )
        }
        {
          this.state.provider.type !== "Adfs" && this.state.provider.type !== "Casdoor" && this.state.provider.type !== "Okta" ? null : (
            <Row style={{marginTop: "20px"}} >
              <Col style={{marginTop: "5px"}} span={2}>
                {Setting.getLabel(i18next.t("provider:Domain"), i18next.t("provider:Domain - Tooltip"))} :
              </Col>
              <Col span={22} >
                <Input value={this.state.provider.domain} onChange={e => {
                  this.updateProviderField("domain", e.target.value);
                }} />
              </Col>
            </Row>
          )
        }
        {this.state.provider.category === "Storage" ? (
          <div>
            {["MinIO"].includes(this.state.provider.type) ?
              (
                <React.Fragment>
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={2}>
                      {Setting.getLabel(i18next.t("provider:MinIO Endpoint"), i18next.t("provider:Region endpoint for Internet"), true)} :
                    </Col>
                    <Col span={22} >
                      <Input maxLength={200} showCount={true}
                        value={this.state.provider.endpoint} onChange={e => {
                          this.updateProviderField("endpoint", e.target.value);
                        }} />
                    </Col>
                  </Row>
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={2}>
                      {Setting.getLabel(i18next.t("provider:Bucket"), i18next.t("provider:Bucket - Tooltip"), true)} :
                    </Col>
                    <Col span={22} >
                      <Input maxLength={200} showCount={true}
                        value={this.state.provider.bucket} onChange={e => {
                          this.updateProviderField("bucket", e.target.value);
                        }} />
                    </Col>
                  </Row>
                </React.Fragment>
              ) : null}
            {["MinIO"].includes(this.state.provider.type) ? null :
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={2}>
                  {Setting.getLabel(i18next.t("provider:Domain"), i18next.t("provider:Domain - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <Input maxLength={200} showCount={true}
                    value={this.state.provider.domain} onChange={e => {
                      this.updateProviderField("domain", e.target.value);
                    }} />
                </Col>
              </Row>
            }
            {["AWS S3", "Tencent Cloud COS"].includes(this.state.provider.type) ? (
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={2}>
                  {Setting.getLabel(i18next.t("provider:Region ID"), i18next.t("provider:Region ID - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <Input maxLength={200} showCount={true}
                    value={this.state.provider.regionId} onChange={e => {
                      this.updateProviderField("regionId", e.target.value);
                    }} />
                </Col>
              </Row>
            ) : null}
          </div>
        ) : null}
        {
          this.state.provider.category === "Email" ? (
            <React.Fragment>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Host"), i18next.t("provider:Host - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <Input maxLength={50} showCount={true}
                    prefix={<LinkOutlined />} value={this.state.provider.host} onChange={e => {
                      this.updateProviderField("host", e.target.value);
                    }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Port"), i18next.t("provider:Port - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <InputNumber maxLength={10} value={this.state.provider.port} onChange={value => {
                    this.updateProviderField("port", value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Disable SSL"), i18next.t("provider:Disable SSL - Tooltip"))} :
                </Col>
                <Col span={1} >
                  <Switch checked={this.state.provider.disableSsl} onChange={checked => {
                    this.updateProviderField("disableSsl", checked);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Email Title"), i18next.t("provider:Email Title - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <Input maxLength={50} showCount={true}
                    value={this.state.provider.title} onChange={e => {
                      this.updateProviderField("title", e.target.value);
                    }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Email Content"), i18next.t("provider:Email Content - Tooltip"), true)} :
                </Col>
                <Col span={22} >
                  <TextArea maxLength={"200"} showCount={true}
                    autoSize={{minRows: 3, maxRows: 100}} value={this.state.provider.content} onChange={e => {
                      this.updateProviderField("content", e.target.value);
                    }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Test Email"), i18next.t("provider:Test Email - Tooltip"))} :
                </Col>
                <Col span={4} >
                  <Input maxLength={50} showCount={true}
                    value={this.state.provider.receiver} placeholder = {i18next.t("user:Input your email")} onChange={e => {
                      this.updateProviderField("receiver", e.target.value);
                    }} />
                </Col>
                <Button style={{marginLeft: "10px", marginBottom: "5px"}} type="primary" onClick={() => {
                  if (this.state.provider.receiver.length > 50) {
                    Setting.showMessage("error",
                      `${i18next.t("provider:Test Email")}${i18next.t("provider:is limited in {number} characters").replace("{number}", 50)}`);
                    return;
                  }
                  ProviderEditTestEmail.connectSmtpServer(this.state.provider);

                }
                } >
                  {i18next.t("provider:Test Connection")}
                </Button>
                <Button style={{marginLeft: "10px", marginBottom: "5px"}} type="primary"
                  disabled={!Setting.isValidEmail(this.state.provider.receiver)}
                  onClick={() => {
                    ProviderEditTestEmail.sendTestEmail(this.state.provider, this.state.provider.receiver);
                  }} >
                  {i18next.t("provider:Send Test Email")}
                </Button>
              </Row>
            </React.Fragment>
          ) : this.state.provider.category === "SMS" ? (
            <React.Fragment>
              {this.state.provider.type === "Twilio SMS" ||
               this.state.provider.type === "smsYd" ?
                null :
                (<Row style={{marginTop: "20px"}} >
                  <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                    {Setting.getLabel(i18next.t("provider:Sign Name"), i18next.t("provider:Sign Name - Tooltip"), true)} :
                  </Col>
                  <Col span={22} >
                    <Input maxLength={20} showCount={true}
                      value={this.state.provider.signName} onChange={e => {
                        this.updateProviderField("signName", e.target.value);
                      }} />
                  </Col>
                </Row>
                )
              }
              {
                this.state.provider.type === "smsYd" ? null :
                  <Row style={{marginTop: "20px"}} >
                    <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                      {this.getTemplateCodeLabel()} :
                    </Col>
                    <Col span={22} >
                      <Input maxLength={50} showCount={true}
                        value={this.state.provider.templateCode} onChange={e => {
                          this.updateProviderField("templateCode", e.target.value);
                        }} />
                    </Col>
                  </Row>
              }
            </React.Fragment>
          ) : this.state.provider.category === "SAML" ? (
            <React.Fragment>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Sign request"), i18next.t("provider:Sign request - Tooltip"))} :
                </Col>
                <Col span={22} >
                  <Switch checked={this.state.provider.enableSignAuthnRequest} onChange={checked => {
                    this.updateProviderField("enableSignAuthnRequest", checked);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Metadata"), i18next.t("provider:Metadata - Tooltip"))} :
                </Col>
                <Col span={22}>
                  <TextArea rows={4} value={this.state.provider.metadata} onChange={e => {
                    this.updateProviderField("metadata", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}}>
                <Col style={{marginTop: "5px"}} span={2} />
                <Col span={2}>
                  <Button type="primary" onClick={() => {
                    try {
                      this.loadSamlConfiguration();
                      Setting.showMessage("success", i18next.t("provider:Parse Metadata successfully"));
                    } catch (err) {
                      Setting.showMessage("error", i18next.t("provider:Can not parse Metadata"));
                    }
                  }}>
                    {i18next.t("provider:Parse")}
                  </Button>
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Endpoint"), i18next.t("provider:SAML 2.0 Endpoint (HTTP)"))} :
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.endpoint} onChange={e => {
                    this.updateProviderField("endpoint", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:IdP"), i18next.t("provider:IdP certificate"))} :
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.idP} onChange={e => {
                    this.updateProviderField("idP", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:Issuer URL"), i18next.t("provider:Issuer URL - Tooltip"))} :
                </Col>
                <Col span={22} >
                  <Input value={this.state.provider.issuerUrl} onChange={e => {
                    this.updateProviderField("issuerUrl", e.target.value);
                  }} />
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:SP ACS URL"), i18next.t("provider:SP ACS URL - Tooltip"))} :
                </Col>
                <Col span={21} >
                  <Input value={`${authConfig.serverUrl}/api/acs`} readOnly="readonly" />
                </Col>
                <Col span={1}>
                  <Button type="primary" onClick={() => {
                    copy(`${authConfig.serverUrl}/api/acs`);
                    Setting.showMessage("success", i18next.t("provider:Link copied to clipboard successfully"));
                  }}>
                    {i18next.t("provider:Copy")}
                  </Button>
                </Col>
              </Row>
              <Row style={{marginTop: "20px"}} >
                <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                  {Setting.getLabel(i18next.t("provider:SP Entity ID"), i18next.t("provider:SP ACS URL - Tooltip"))} :
                </Col>
                <Col span={21} >
                  <Input value={`${authConfig.serverUrl}/api/acs`} readOnly="readonly" />
                </Col>
                <Col span={1}>
                  <Button type="primary" onClick={() => {
                    copy(`${authConfig.serverUrl}/api/acs`);
                    Setting.showMessage("success", i18next.t("provider:Link copied to clipboard successfully"));
                  }}>
                    {i18next.t("provider:Copy")}
                  </Button>
                </Col>
              </Row>
            </React.Fragment>
          ) : null
        }
        {this.getAppIdRow()}
        {/* <Row style={{marginTop: "20px"}} >
          <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
            {Setting.getLabel(i18next.t("provider:Provider URL"), i18next.t("provider:Provider URL - Tooltip"))} :
          </Col>
          <Col span={22} >
            <Input prefix={<LinkOutlined />} value={this.state.provider.providerUrl} onChange={e => {
              this.updateProviderField("providerUrl", e.target.value);
            }} />
          </Col>
        </Row> */}
        {
          this.state.provider.category !== "Captcha" ? null : (
            <Row style={{marginTop: "20px"}} >
              <Col style={{marginTop: "5px"}} span={(Setting.isMobile()) ? 22 : 2}>
                {Setting.getLabel(i18next.t("general:Preview"), i18next.t("general:Preview - Tooltip"))} :
              </Col>
              <Col span={22} >
                <CaptchaPreview
                  provider={this.state.provider}
                  providerId={this.state.providerId}
                  clientSecret={this.state.provider.clientSecret}
                  captchaType={this.state.provider.type}
                  subType={this.state.provider.subType}
                  owner={this.state.provider.owner}
                  clientId={this.state.provider.clientId}
                  name={this.state.provider.uid}
                  providerUrl={this.state.provider.providerUrl}
                  clientId2={this.state.provider.clientId2}
                  clientSecret2={this.state.provider.clientSecret2}
                />
              </Col>
            </Row>
          )
        }
      </Card>
    );
  }

  submitProviderEdit(willExist) {
    const provider = Setting.deepCopy(this.state.provider);

    if (provider.displayName === "") {
      Setting.showMessage("error", i18next.t("provider:Display name empty"));
      return;
    }

    if (provider.type === "WeChat") {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:AppID empty"));
        return;
      }

      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:App Secret empty"));
        return;
      }
    }

    if (provider.type === "WeCom") {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:CorpID empty"));
        return;
      }

      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:Secret empty"));
        return;
      }

      if (provider.appId === "") {
        Setting.showMessage("error", i18next.t("provider:Agent ID empty"));
        return;
      }
    }

    if (provider.type === "Aliyun SMS") {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:AccessKey ID empty"));
        return;
      }

      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:AccessKey Secret empty"));
        return;
      }

      if (provider.signName === "") {
        Setting.showMessage("error", i18next.t("provider:Sign Name empty"));
        return;
      }
    }

    if (provider.type === "Tencent Cloud SMS") {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:Secret ID empty"));
        return;
      }

      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:Secret Key empty"));
        return;
      }

      if (provider.signName === "") {
        Setting.showMessage("error", i18next.t("provider:Sign Name empty"));
        return;
      }
    }

    if (provider.category === "Email") {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:Username empty"));
        return;
      }

      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:Password empty"));
        return;
      }

      if (provider.host === "") {
        Setting.showMessage("error", i18next.t("provider:Host empty"));
        return;
      }

      if (provider.title === "") {
        Setting.showMessage("error", i18next.t("provider:Email Title empty"));
        return;
      }

      if (provider.content === "") {
        Setting.showMessage("error", i18next.t("provider:Email Content emtpy"));
        return;
      }
    }

    if (provider.type === "Local File System" && provider.domain === "") {
      Setting.showMessage("error", i18next.t("provider:Domain empty"));
      return;
    }

    if (["MinIO"].includes(provider.type)) {
      if (provider.clientId === "") {
        Setting.showMessage("error", i18next.t("provider:MinIO Client ID empty"));
        return;
      }
      if (provider.clientSecret === "") {
        Setting.showMessage("error", i18next.t("provider:MinIO Client Secret empty"));
        return;
      }
      if (provider.endpoint === "") {
        Setting.showMessage("error", i18next.t("provider:MinIO Endpoint empty"));
        return;
      }
      if ((provider.bucket === "")) {
        Setting.showMessage("error", i18next.t("provider:Bucket empty"));
        return;
      }
      if (provider.regionId === "") {
        Setting.showMessage("error", i18next.t("provider:Region ID empty"));
        return;
      }
    }

    ProviderBackend.updateProvider(this.state.provider.owner, this.state.providerId, provider)
      .then((res) => {
        if (res.msg === "") {
          Setting.showMessage("success", i18next.t("general:Successfully saved"));
          this.setState({
            providerId: this.state.provider.uid,
          });

          if (willExist) {
            this.props.history.push("/providers");
          } else {
            this.setState({mode: "edit"});
            this.props.history.replace(`/providers/${this.state.provider.uid}`);
          }
        } else {
          Setting.showResponseMessage(res.msg);
          this.updateProviderField("uid", this.state.providerId);
        }
      })
      .catch(error => {
        Setting.showResponseMessage(error);
      });
  }

  deleteProvider() {
    ProviderBackend.deleteProvider(this.state.provider)
      .then(() => {
        this.props.history.push("/providers");
      })
      .catch(error => {
        Setting.showMessage("error", `Provider failed to delete: ${error}`);
      });
  }

  exit() {
    this.props.history.push("/providers");
  }

  render() {
    return (
      <div>
        {
          this.state.provider !== null ? this.renderProvider() : null
        }
        <div style={{marginTop: "20px", marginLeft: "40px"}}>
          <Button size="large" onClick={() => this.submitProviderEdit(false)}>{i18next.t("general:Save")}</Button>
          <Button style={{marginLeft: "20px"}} type="primary" size="large" onClick={() => this.submitProviderEdit(true)}>{i18next.t("general:Save & Exit")}</Button>
          {this.state.mode === "add" ? <Button style={{marginLeft: "20px"}} size="large" onClick={() => this.deleteProvider()}>{i18next.t("general:Cancel")}</Button> :
            <Button style={{marginLeft: "20px"}} onClick={() => this.exit()}>{i18next.t("general:Cancel")}</Button>}
        </div>
      </div>
    );
  }
}

export default ProviderEditPage;
